import { graphql } from "babel-plugin-relay/macro";
import { Card } from "primereact/card";
import React, { useState } from "react";
import { useFragment, useMutation } from "react-relay";
import { StartTreeConfigEditor_RemoveStartTreeConfigMutation } from "../../../../../../../__generated__/StartTreeConfigEditor_RemoveStartTreeConfigMutation.graphql";
import {
	StartTreeConfigEditor_TreeNodeFragment$key,
	StartTreeConfigType,
} from "../../../../../../../__generated__/StartTreeConfigEditor_TreeNodeFragment.graphql";
import {
	ExtendStartTreeConfigs,
	ProjectExtendStartTreeConfigEditDialogs,
} from "../../../../../../../lms-admin-impl/components/relay/ProjectExtendStartTreeConfigEditDialogs";
import {
	formatDateTimeAsCalendarDate,
	utcFormatDateTime,
} from "../../../../../core/datetime/DateTimeDisplay";
import {
	ConfigurationGraphqlInterface,
	Config,
	ConfigCustomLabel,
} from "../components/Config.interfaces";
import { ConfigDropdown, ConfigDropdownOptions } from "../components/ConfigDropdown";
import { ConfigItem } from "../components/ConfigItem";
import { ConfigPreview } from "../components/ConfigPreview";

const TREE_NODE_FRAGMENT = graphql`
	fragment StartTreeConfigEditor_TreeNodeFragment on TreeNode {
		id
		structureDefinition {
			... on RootStructureDefinition {
				startConfigs {
					id
					configType
					...ProjectExtendStartTreeConfigEditDialogs_StartTreeConfigFragment
				}
			}
		}
		...ProjectExtendStartTreeConfigEditDialogs_TreeNodeFragment
	}
`;

const REMOVE_START_TREE_CONDITION_CONFIGURATION_MUTATION = graphql`
	mutation StartTreeConfigEditor_RemoveStartTreeConfigMutation(
		$input: RemoveStartTreeConfigInput!
	) {
		Admin {
			Tree {
				removeStartTreeConfig(input: $input) {
					rootNode {
						...StartTreeConfigEditor_TreeNodeFragment
					}
				}
			}
		}
	}
`;

interface Props {
	treeNodeFragmentRef: StartTreeConfigEditor_TreeNodeFragment$key;
}

export const StartTreeConfigEditor = ({ treeNodeFragmentRef }: Props) => {
	const treeNode = useFragment<StartTreeConfigEditor_TreeNodeFragment$key>(
		TREE_NODE_FRAGMENT,
		treeNodeFragmentRef,
	);
	const [removeStartTreeConfig, isRemovingStartTreeConfig] =
		useMutation<StartTreeConfigEditor_RemoveStartTreeConfigMutation>(
			REMOVE_START_TREE_CONDITION_CONFIGURATION_MUTATION,
		);

	const [previewOptions, setPreviewOptions] = useState<
		ConfigDropdownOptions<StartTreeConfigType>[]
	>([]);

	let labels: ConfigCustomLabel<StartTreeConfigType>[];

	const configs: Config<StartTreeConfigType, any | never>[] = [...ExtendStartTreeConfigs];

	const options: ConfigDropdownOptions<StartTreeConfigType>[] = configs.map((c) => {
		return {
			label: c.configKey,
			value: c.configKey,
		};
	});

	const setLabels = (customLabels: ConfigCustomLabel<StartTreeConfigType>[]) => {
		labels = customLabels;

		setPreviewOptions(
			configs.map((c) => {
				const selectedConfig = treeNode.structureDefinition.startConfigs?.find(
					(r: any) => r.configType === c.configKey,
				);
				const customLabel = labels.find((l) => l.configKey === c.configKey);

				if (selectedConfig && customLabel?.label) {
					return {
						label: `(${formatDateTimeAsCalendarDate(customLabel.label, false)})`,
						value: c.configKey,
					};
				} else {
					return {
						label: c.configKey,
						value: c.configKey,
					};
				}
			}),
		);
	};

	return (
		<Card className="mb-2">
			<h2>Starten</h2>

			<ConfigDropdown<StartTreeConfigType>
				configOptions={options}
				onChange={(e) => {
					const selectedConfig = configs.find((c) => c.configKey === e.value);

					if (selectedConfig) {
						selectedConfig.addMutation({
							rootNodeId: treeNode.id,
							...selectedConfig.addMutationPayload,
						});
					}
				}}
				isPresentational={false}
			/>

			<ConfigPreview<ConfigurationGraphqlInterface<StartTreeConfigType>>
				dataKey={"id"}
				selectedConfigs={treeNode.structureDefinition.startConfigs as any[]}
				template={(configuration: ConfigurationGraphqlInterface<StartTreeConfigType>) => (
					<>
						<ProjectExtendStartTreeConfigEditDialogs
							treeNodeFragmentRef={treeNode}
							// @ts-ignore
							configurationFragmentRef={configuration}
							setCustomLabels={(customLabels: any) => setLabels(customLabels)}
						/>

						<ConfigItem<StartTreeConfigType, string>
							isPresentational={true}
							isLoading={isRemovingStartTreeConfig}
							configType={configuration.configType}
							canEdit={
								configs.find((c) => c.configKey === configuration.configType)
									?.editable as boolean
							}
							configOptions={previewOptions}
							onDelete={() => {
								removeStartTreeConfig({
									variables: {
										input: {
											rootNodeId: treeNode.id,
											configId: configuration.id,
										},
									},
								});
							}}
							editDialog={(props: any) => (
								<>
									<ProjectExtendStartTreeConfigEditDialogs
										{...props}
										treeNodeFragmentRef={treeNode}
										// @ts-ignore
										configurationFragmentRef={configuration}
									/>
								</>
							)}
						/>
					</>
				)}
			/>
		</Card>
	);
};
